import { useCallback, useState } from 'react';
import type { ViewSpec } from './types';

const MAX_HISTORY = 20;

/**
 * Angre/gjør om for en Compass-visning i økten. Første versjon (Compass-forslaget) beholdes for «Tilbakestill».
 * Brukes av chat-svar og rapportvisning — både direkte endringer (graftype) og justeringer via Compass.
 * `originalView`: Compass-forslaget når `initial` allerede er en justert versjon
 * (resultatpanelet monteres på nytt når brukeren bytter mellom resultater).
 */
export function useViewHistory(initial: ViewSpec | undefined, originalView?: ViewSpec) {
    const [original] = useState<ViewSpec | undefined>(originalView ?? initial);
    const [stack, setStack] = useState<ViewSpec[]>(initial ? [initial] : []);
    const [index, setIndex] = useState(0);

    const view = stack[index];

    const push = useCallback((next: ViewSpec) => {
        setStack(prev => [...prev.slice(Math.max(0, index + 1 - MAX_HISTORY), index + 1), next]);
        setIndex(i => Math.min(i + 1, MAX_HISTORY));
    }, [index]);

    const undo = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);
    const redo = useCallback(() => setIndex(i => Math.min(stack.length - 1, i + 1)), [stack.length]);
    const reset = useCallback(() => {
        if (original) push(original);
    }, [original, push]);

    return {
        view,
        push,
        undo,
        redo,
        reset,
        canUndo: index > 0,
        canRedo: index < stack.length - 1,
        isModified: !!original && view !== original && JSON.stringify(view) !== JSON.stringify(original)
    };
}
